import * as React from 'react';

import {useQuery} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {ErrorState} from 'shells/error-state/error-state';
import {SpacerGroup} from 'shells/spacer/spacer-group';
import {UppercaseLabel} from 'shells/typography/uppercase-label';

import type {
    GetEditionEngagementStats as GetEditionEngagementStatsQuery,
    GetEditionEngagementStatsVariables as GetEditionEngagementStatsQueryVariables,
    GetEditionStatus as GetEditionStatusQuery,
    GetEditionStatusVariables as GetEditionStatusQueryVariables,
    EditionFragment as Edition,
} from 'nutshell-graphql-types';

import type {ReportURLParams} from '../../../reports/report-types';

import {ComparisonEngagementStats} from '../../engagement/comparison-engagement-stats';
import {EngagementBar} from '../../engagement/engagement-bar';
import {EDITION_FILTER_KEYS, updateSearchWithNewFilter} from '../../utils';

import GET_EDITION_ENGAGEMENT_STATS from '../graphql/queries/get-edition-engagement-stats.graphql';

import {EditionEngagementReport} from './edition-engagement-report';
import {EditionLinkTrackingTable} from './edition-link-tracking-table';
import {EditionSendingState} from './edition-sending-state';
import GET_EDITION_STATUS from './graphql/queries/edition-status.graphql';

import styles from './edition-engagement.module.css';

type Props = {
    edition: Edition;
    onParamChange: (arg1: any, arg2: boolean) => any;
    onParamPush: (arg1: any, arg2: boolean) => any;
    urlParams: ReportURLParams<'contacts'>;
    neverShowSendingState?: boolean;
};

export function EditionEngagement(props: Props) {
    const {
        data,
        loading: isLoading,
        error,
    } = useQuery<GetEditionEngagementStatsQuery, GetEditionEngagementStatsQueryVariables>(
        GET_EDITION_ENGAGEMENT_STATS,
        {
            variables: {editionId: props.edition.id},
        }
    );

    const {data: editionStatusData} = useQuery<
        GetEditionStatusQuery,
        GetEditionStatusQueryVariables
    >(GET_EDITION_STATUS, {
        variables: {editionId: props.edition.id},
        // Don't poll the edition status if we're not sending an email. This
        // could clobber our servers if everyone did this all the time
        pollInterval: getPollTimeout(props.edition),
    });

    const editionStatus =
        editionStatusData && editionStatusData.edition
            ? editionStatusData.edition.status
            : props.edition.status;

    if (error) {
        // @ts-expect-error
        return <ErrorState errorMessage={safelyGetGraphQLErrorMessage(error) || undefined} />;
    }

    // If sending and less than 1% of emails have been delivered...
    const shouldShowSendingState =
        editionStatus === 'SENDING' &&
        !props.neverShowSendingState &&
        (!props.edition.audience ||
            (props.edition.__typename === 'Edition' &&
                props.edition.numSentEmails <
                    props.edition.audience.membershipStats.totalCount.value / 100));

    if (shouldShowSendingState) {
        return <EditionSendingState edition={props.edition} />;
    }

    const canEditNotifiables =
        props.edition.campaign.type !== 'DRIP_SEQUENCE' ||
        (props.edition.campaign.type === 'DRIP_SEQUENCE' &&
            props.edition.status !== 'RETIRING' &&
            props.edition.status !== 'RETIRED' &&
            props.edition.status !== 'CANCELLED');

    const shouldShowCounts =
        props.edition.campaign.type === 'DRIP_SEQUENCE' ||
        (props.edition.status !== 'NEW' && props.edition.status !== 'SCHEDULED');

    return (
        <div className={styles.container}>
            <div>
                <SpacerGroup>
                    {data &&
                    data.edition &&
                    data.edition.__typename === 'Edition' &&
                    data.edition.comparison &&
                    data.edition.comparisonPeriodEngagementStats &&
                    data.edition.comparisonEdition &&
                    data.edition.comparisonEdition.__typename === 'Edition' &&
                    data.edition.comparisonEdition.comparisonPeriodEngagementStats &&
                    shouldShowCounts ? (
                        <ComparisonEngagementStats
                            editionStatus={editionStatus}
                            versionAId={data.edition.id}
                            versionBId={data.edition.comparisonEdition.id}
                            versionAStats={data.edition.comparisonPeriodEngagementStats}
                            versionBStats={
                                data.edition.comparisonEdition.comparisonPeriodEngagementStats
                            }
                            comparison={data.edition.comparison}
                            isLoading={isLoading}
                        />
                    ) : (
                        <EngagementBar
                            infoText='Synced emails are not included in these totals'
                            engagementStats={
                                data && data.edition ? data.edition.engagementStats : null
                            }
                            isLoading={isLoading}
                            onClickStat={
                                editionStatus === 'SENT' || editionStatus === 'HALTED'
                                    ? (filterKey: string) => {
                                          const newSearch = updateSearchWithNewFilter(
                                              filterKey,
                                              props.edition.id,
                                              location.search,
                                              // @ts-expect-error
                                              EDITION_FILTER_KEYS
                                          );

                                          // This is the relative path needed for the Link to work
                                          return `recipients?${newSearch}`;
                                      }
                                    : undefined
                            }
                        />
                    )}
                </SpacerGroup>
                <SpacerGroup>
                    <div className='mb-8'>
                        <UppercaseLabel>Engagement</UppercaseLabel>
                    </div>
                    <EditionEngagementReport
                        onParamChange={props.onParamChange}
                        onParamPush={props.onParamPush}
                        urlParams={props.urlParams}
                        edition={props.edition}
                    />
                </SpacerGroup>
                {props.edition.emailTemplate ? (
                    // The email template should never be missing at this point, but just in case
                    <SpacerGroup>
                        <div className='mb-8'>
                            <UppercaseLabel>
                                Link tracking
                                {props.edition.comparisonEdition ? ' Version A' : undefined}
                            </UppercaseLabel>
                        </div>
                        <EditionLinkTrackingTable
                            editionId={props.edition.id}
                            emailTemplateId={props.edition.emailTemplate.id}
                            shouldShowCounts={shouldShowCounts}
                            canEditNotifiables={canEditNotifiables}
                        />
                    </SpacerGroup>
                ) : undefined}
                {props.edition.comparisonEdition &&
                props.edition.comparisonEdition.emailTemplate ? (
                    <SpacerGroup>
                        <div className='mb-8'>
                            <UppercaseLabel>Link tracking Version B</UppercaseLabel>
                        </div>
                        <EditionLinkTrackingTable
                            editionId={props.edition.comparisonEdition.id}
                            emailTemplateId={props.edition.comparisonEdition.emailTemplate.id}
                            shouldShowCounts={shouldShowCounts}
                            canEditNotifiables={canEditNotifiables}
                        />
                    </SpacerGroup>
                ) : undefined}
            </div>
        </div>
    );
}

function getPollTimeout(edition: Edition) {
    return edition.status === 'SENDING' && edition.campaign.type !== 'DRIP_SEQUENCE'
        ? 5000
        : undefined;
}
